import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../../lib/supabase";
import { useRealtimeTable } from "../../lib/useRealtimeTable";
import type { Deal } from "../../lib/types";
import DealLink from "../DealLink";
import DealCallText from "../DealCallText";
import DispatchQuestionPill from "./comms/DispatchQuestionPill";
import RouteMap from "../shared/RouteMap";
import type { RouteStop } from "../shared/RouteMap";
import { mapsHref, isAddressLinkable } from "../../lib/mapsHref";

/**
 * Pre-Inspections tab (Roof Specialist board).
 *
 * Shows the open pre-inspection pool — deals at stage `pre_inspection_ready`
 * that still need a roof climb. Pre-inspections assigned to the RS whose board
 * this is get a "YOU" tag. The list can be sorted by the RS's current GPS
 * location so that after finishing an adjuster meeting they can grab the
 * closest / in-route pre-inspection. A map preview shows the candidates (with
 * the RS's location as the start pin) to eyeball what's on the way.
 *
 * Pool definition matches the Jun 8 pre-inspection reshape: stage =
 * 'pre_inspection_ready' AND pre_inspection_completed != true. Widen the stage
 * set here if roofs at other stages should surface as nearby pre-insps.
 */

const COLS =
  "id,homeowner_name,homeowner_phone,homeowner_email,property_street,property_city,property_state,property_zip,property_lat,property_lon,insurance_company,stage,phase,owner_id,split_rm_id,assigned_rs_id,pre_inspection_assigned_to,pre_inspection_date";

type SortMode = "mine" | "distance";

function formatAddress(d: Deal): string {
  return [
    d.property_street,
    [d.property_city, d.property_state].filter(Boolean).join(", "),
    d.property_zip,
  ]
    .filter(Boolean)
    .join(" ");
}

function haversineMi(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // miles
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

interface Props {
  /** team_roster id of the RS whose board this is — drives the YOU tag. */
  rsId?: string;
  rsName?: string;
}

type Row = {
  deal: Deal;
  mine: boolean;
  assignedFirst: string | null;
  distanceMi: number | null;
};

export default function PreInspectionsTab({ rsId, rsName }: Props) {
  const [deals, setDeals] = useState<Deal[] | null>(null);
  const [memberNames, setMemberNames] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const [myLoc, setMyLoc] = useState<{ lat: number; lon: number } | null>(null);
  const [locStatus, setLocStatus] = useState<"idle" | "loading" | "error">("idle");
  const [sortMode, setSortMode] = useState<SortMode>("mine");

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  useRealtimeTable("deals", reload);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setError(null);
      // Pool = deals in the pre_inspection_ready stage. Completing a
      // pre-inspection advances the deal to adjuster_meeting_ready, so the
      // stage alone defines "still needs a climb" — no boolean filter needed
      // (and pre_inspection_completed is nullable, so a `.neq(...,true)` filter
      // would silently drop NULL rows via three-valued logic). Matches the
      // open-pre pool ScheduleTab uses.
      const { data, error: e } = await supabase
        .from("deals")
        .select(COLS)
        .eq("stage", "pre_inspection_ready");
      if (e) {
        if (!cancelled) setError(e.message);
        return;
      }
      const rows = (data ?? []) as Deal[];

      // Resolve names of whoever the pre-insps are assigned to (for the
      // non-mine rows so the RS can see who's on it).
      const ids = new Set<string>();
      rows.forEach((d) => {
        const a = d.assigned_rs_id ?? d.pre_inspection_assigned_to;
        if (a) ids.add(a);
      });
      const names: Record<string, string> = {};
      if (ids.size) {
        const { data: ms } = await supabase
          .from("team_roster")
          .select("id,full_name")
          .in("id", [...ids]);
        (ms ?? []).forEach((m: { id: string; full_name: string }) => {
          names[m.id] = m.full_name;
        });
      }
      if (!cancelled) {
        setDeals(rows);
        setMemberNames(names);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [reloadKey]);

  const requestLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocStatus("error");
      return;
    }
    setLocStatus("loading");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setMyLoc({ lat: pos.coords.latitude, lon: pos.coords.longitude });
        setLocStatus("idle");
        setSortMode("distance");
      },
      () => setLocStatus("error"),
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 },
    );
  }, []);

  const rows: Row[] = useMemo(() => {
    if (!deals) return [];
    const assignedTo = (d: Deal) => d.assigned_rs_id ?? d.pre_inspection_assigned_to ?? null;
    const built: Row[] = deals.map((d) => {
      const a = assignedTo(d);
      const mine =
        !!rsId && (d.assigned_rs_id === rsId || d.pre_inspection_assigned_to === rsId);
      const distanceMi =
        myLoc && d.property_lat != null && d.property_lon != null
          ? haversineMi(myLoc.lat, myLoc.lon, d.property_lat, d.property_lon)
          : null;
      return {
        deal: d,
        mine,
        assignedFirst: a && !mine ? memberNames[a]?.split(" ")[0] ?? null : null,
        distanceMi,
      };
    });

    const byDistance = (x: Row, y: Row) => {
      if (x.distanceMi == null && y.distanceMi == null)
        return (x.deal.homeowner_name ?? "").localeCompare(y.deal.homeowner_name ?? "");
      if (x.distanceMi == null) return 1;
      if (y.distanceMi == null) return -1;
      return x.distanceMi - y.distanceMi;
    };

    if (sortMode === "distance" && myLoc) {
      built.sort(byDistance);
    } else {
      // Mine first, then nearest (if located) / alphabetical.
      built.sort((x, y) => {
        if (x.mine !== y.mine) return x.mine ? -1 : 1;
        return byDistance(x, y);
      });
    }
    return built;
  }, [deals, rsId, myLoc, sortMode, memberNames]);

  const mineCount = useMemo(() => rows.filter((r) => r.mine).length, [rows]);
  const unassignedCount = useMemo(
    () => rows.filter((r) => !r.deal.assigned_rs_id && !r.deal.pre_inspection_assigned_to).length,
    [rows],
  );

  // Map preview — candidates in current order, with the RS's location as the
  // start pin so the route line shows what's on the way.
  const routeStops: RouteStop[] = useMemo(() => {
    const stops: RouteStop[] = [];
    if (myLoc) {
      stops.push({ label: "★", name: rsName ? `${rsName} (you)` : "You", address: "Your current location", lat: myLoc.lat, lon: myLoc.lon });
    }
    rows.forEach((r, i) => {
      if (r.deal.property_lat == null || r.deal.property_lon == null) return;
      stops.push({
        label: String(i + 1),
        name: r.deal.homeowner_name ?? "(no name)",
        address: formatAddress(r.deal),
        lat: r.deal.property_lat,
        lon: r.deal.property_lon,
      });
    });
    return stops;
  }, [rows, myLoc, rsName]);

  if (error)
    return (
      <div className="bg-red-50 text-red-700 p-4 rounded">
        Couldn't load pre-inspections — {error}
      </div>
    );
  if (!deals) return <div className="text-slate-500 p-4">Loading pre-inspections…</div>;

  return (
    <div>
      {/* KPI Row — "Yours" only shown on an RS board (rsId set). */}
      <div className={"grid grid-cols-2 gap-2 mb-3 " + (rsId ? "md:grid-cols-3" : "md:grid-cols-2")}>
        <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
          <div className="text-xl font-extrabold text-slate-900">{rows.length}</div>
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Open Pre-Insp</div>
        </div>
        {rsId && (
          <div className="bg-teal-50 border border-teal-200 rounded-lg p-2.5 text-center">
            <div className="text-xl font-extrabold text-teal-700">{mineCount}</div>
            <div className="text-[9px] font-semibold text-teal-600 uppercase tracking-wide">Yours</div>
          </div>
        )}
        <div className="bg-white border border-slate-200 rounded-lg p-2.5 text-center">
          <div className="text-xl font-extrabold text-amber-600">{unassignedCount}</div>
          <div className="text-[9px] font-semibold text-slate-500 uppercase tracking-wide">Unassigned</div>
        </div>
      </div>

      {/* Sort controls */}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-wide">Sort</span>
        {rsId && (
          <button
            onClick={() => setSortMode("mine")}
            className={
              "px-3 py-1 rounded-md text-[11px] font-semibold border transition " +
              (sortMode === "mine"
                ? "bg-bardin-teal text-white border-bardin-teal"
                : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
            }
          >
            Yours first
          </button>
        )}
        <button
          onClick={() => {
            if (myLoc) setSortMode("distance");
            else requestLocation();
          }}
          className={
            "px-3 py-1 rounded-md text-[11px] font-semibold border transition inline-flex items-center gap-1 " +
            (sortMode === "distance" && myLoc
              ? "bg-bardin-teal text-white border-bardin-teal"
              : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50")
          }
        >
          📍 Nearest to me
        </button>
        {myLoc && (
          <button
            onClick={requestLocation}
            className="px-2.5 py-1 rounded-md text-[11px] font-medium text-slate-500 border border-slate-200 bg-white hover:bg-slate-50"
            title="Refresh your location"
          >
            ↻ Update location
          </button>
        )}
        {locStatus === "loading" && <span className="text-[11px] text-slate-400">Locating…</span>}
        {locStatus === "error" && (
          <span className="text-[11px] text-red-500">Location unavailable — enable GPS / allow access.</span>
        )}
      </div>

      {/* Map preview */}
      {routeStops.length > 0 ? (
        <RouteMap stops={routeStops} minutesPerStop={0} perStopLabel="" />
      ) : (
        <div
          className="mb-4 relative rounded-xl overflow-hidden border border-slate-200 h-40"
          style={{ background: "linear-gradient(135deg, #e0f2fe 0%, #f0fdf4 50%, #fefce8 100%)" }}
        >
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-slate-500">
            <span className="text-3xl">🗺️</span>
            <span className="text-[12px] font-medium">
              {rows.length === 0 ? "No open pre-inspections." : "Tap “Nearest to me” to map by your location."}
            </span>
          </div>
        </div>
      )}

      {/* List */}
      {rows.length === 0 ? (
        <div className="bg-white rounded-xl shadow p-8 text-center text-slate-500">
          No open pre-inspections right now.
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          {rows.map((r, idx) => {
            const d = r.deal;
            return (
              <div
                key={d.id}
                className={
                  "flex items-center gap-2 px-3 py-2.5 border-b border-slate-100 last:border-b-0 text-[12px] " +
                  (r.mine ? "bg-amber-50/50 border-l-[3px] border-l-amber-400" : "hover:bg-slate-50")
                }
              >
                {/* Rank circle */}
                <div className="w-[22px] h-[22px] rounded-full bg-amber-500 text-white text-[10px] font-extrabold flex items-center justify-center shrink-0">
                  {idx + 1}
                </div>

                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold shrink-0 bg-amber-100 text-amber-800">
                  PRE
                </span>

                {/* Name + address */}
                <div className="min-w-0 flex-1">
                  <span className="font-semibold text-slate-900">
                    <DealLink id={d.id}>{d.homeowner_name ?? "(no name)"}</DealLink>
                  </span>
                  {isAddressLinkable(d) ? (
                    <a
                      href={mapsHref(d)}
                      target="_blank"
                      rel="noreferrer"
                      className="text-slate-500 text-[11px] ml-1.5 hidden md:inline hover:underline hover:text-slate-700"
                    >
                      {formatAddress(d) || "(no address)"}
                    </a>
                  ) : (
                    <span className="text-slate-500 text-[11px] ml-1.5 hidden md:inline">
                      {formatAddress(d) || "(no address)"}
                    </span>
                  )}
                  {d.insurance_company && (
                    <span className="text-slate-400 text-[10px] ml-1.5 hidden lg:inline">· {d.insurance_company}</span>
                  )}
                </div>

                {/* Distance */}
                {r.distanceMi != null && (
                  <span className="text-[10px] font-bold text-teal-700 bg-teal-50 border border-teal-200 rounded px-1.5 py-0.5 shrink-0">
                    {r.distanceMi < 10 ? r.distanceMi.toFixed(1) : Math.round(r.distanceMi)} mi
                  </span>
                )}

                {/* Assignment */}
                {r.mine ? (
                  <span className="px-1.5 py-0.5 rounded text-[9px] font-extrabold bg-amber-400 text-amber-950 shrink-0 tracking-wide">
                    YOU
                  </span>
                ) : (
                  <span className="text-slate-500 text-[11px] hidden md:inline shrink-0">
                    {r.assignedFirst ?? "Unassigned"}
                  </span>
                )}

                {/* Quick actions */}
                <div className="flex gap-1 shrink-0 ml-1 items-center">
                  <a
                    href={mapsHref(d)}
                    target="_blank"
                    rel="noreferrer"
                    className="w-7 h-7 rounded-full bg-white border border-slate-200 flex items-center justify-center text-[12px] hover:bg-slate-50 transition"
                    title="Navigate"
                  >
                    📍
                  </a>
                  <DealCallText
                    dealId={d.id}
                    homeownerName={d.homeowner_name}
                    homeownerPhone={d.homeowner_phone}
                    compact
                  />
                  <DispatchQuestionPill
                    deal={{
                      id: d.id,
                      homeowner_name: d.homeowner_name,
                      property_street: d.property_street,
                      owner_id: d.owner_id,
                      split_rm_id: d.split_rm_id ?? null,
                      pre_inspection_assigned_to: d.pre_inspection_assigned_to ?? null,
                      phase: d.phase ?? null,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
